import { createAction, createSlice } from "@reduxjs/toolkit";
import { BasicTDHCalculation, BasicWellInflowCalculation, DepletionCurveGraphData, InflowData, IPRGraphData, IPRMethodology, WellInflowInfo } from "../../model/calculations/calculation";
import { basicSummaryResponse } from "../../model/jetlift/jetlift";
import { LABELS } from "../../../constants/labels/jetlift/jetlift.labels";

interface BasicCalculationState {
    loading: boolean;
    error: string | null;
    BasicWellInflowCalculation: BasicWellInflowCalculation | null,
    IPRSelection: IPRMethodology | "";
    basicTDHCalculation: BasicTDHCalculation | null;
    depletionPressure: number | null;
    depletionPercentage: number | null;
    futureProductionRate: number;
    futureBHP: number | null;
    BasicCalculationComplete: boolean;
    wellInflowData: WellInflowInfo[] | null;
    depletionCurve: DepletionCurveGraphData | null;
    iprCurve: IPRGraphData | null;
    inflowData: InflowData | null;
    showIPRPlotInMainReport: boolean;
    showDepletionPlotInMainReport: boolean;
    basicSummary: basicSummaryResponse | null;
    basicSummaryScenarios: any | null;
    formattedBasicSummaryScenarios?: any;
    cavitationWarning: string | null;
}

export const initialState: BasicCalculationState = {
    loading: false,
    error: null,
    BasicWellInflowCalculation: null,
    IPRSelection: "Wiggins",
    basicTDHCalculation: null,
    depletionPressure: null,
    depletionPercentage: null,
    futureProductionRate: 1000,
    futureBHP: null,
    BasicCalculationComplete: false,
    wellInflowData: [],
    iprCurve: null,
    depletionCurve: null,
    inflowData: null,
    showIPRPlotInMainReport: false,
    showDepletionPlotInMainReport: false,
    basicSummary: null,
    basicSummaryScenarios: null,
    formattedBasicSummaryScenarios: null,
    cavitationWarning: null,
};

export const loadFutureProductionRate = createAction("jetlift/basicCalculation/getFutureProductionRate");
export const loadDepletionPressure = createAction("jetlift/basicCalculation/getDepletionPressure");
export const loadBasicSummary = createAction("jetlift/basicCalculation/loadBasicSummary");
export const loadbasicSummaryScenarios = createAction("jetlift/basicCalculation/loadbasicSummaryScenarios");

const BasicCalculationSlice = createSlice({
    name: "jetlift/basicCalculation",
    initialState,
    reducers: {
        resetBasicCalculations: () => ({
            ...initialState
        }),
        setLoading: (state, action) => {
            state.loading = action?.payload;
        },
        setError: (state, action) => {
            state.error = action?.payload;
        },
        setBasicWellInflowCalculation: (state, action) => {
            state.BasicWellInflowCalculation = action?.payload;
        },
        setBasicWellInflowCalculation2: (state, action) => {
            state.BasicWellInflowCalculation = action?.payload;
        },
        setBasicTDHCalculation: (state, action) => {
            state.basicTDHCalculation = action?.payload;
        },
        setBasicTDHCalculationOutput: (state, action) => {
            state.basicTDHCalculation = {
                ...state.basicTDHCalculation,
                pipNew: action?.payload.pipNew ?? state?.basicTDHCalculation?.pipNew,
                pip: action?.payload.pip ?? state?.basicTDHCalculation?.pip,
                tubingHead: action?.payload.tubingHead ?? state?.basicTDHCalculation?.tubingHead,
                tdh: action?.payload.tdh ?? state?.basicTDHCalculation?.tdh,
                productionLevel: action?.payload.productionLevel ?? state?.basicTDHCalculation?.productionLevel,
                frictionLoss: action?.payload.frictionLoss ?? state?.basicTDHCalculation?.frictionLoss,
                systemTDH: action?.payload.systemTDH,
            }
        },
        setBasicWellIPRDepletionPressure: (state, action) => {
            state.depletionPressure = action?.payload;
        },
        setBasicWellIPRDepletionPercentage: (state, action) => {
            state.depletionPercentage = action?.payload;
        },
        setBasicWellIPRFutureProdRate: (state, action) => {
            state.futureProductionRate = action?.payload;
        },
        setBasicWellIPRFutureBHP: (state, action) => {
            state.futureBHP = action?.payload;
        },
        setBasicCalculationComplete: (state, action) => {
            state.BasicCalculationComplete = action?.payload;
        },
        setBasicWellInflowData: (state, action) => {
            state.wellInflowData = action?.payload;
        },
        setBasicIPRMethodologySelection: (state, action) => {
            state.IPRSelection = action?.payload;
        },
        setBasicTDHDesiredPIP: (state, action) => {
            if (state.basicTDHCalculation) {
                state.basicTDHCalculation = {
                    ...state.basicTDHCalculation,
                    desrdPIP: action?.payload,
                }
            }
        },
        setBasicNewTDH: (state, action) => {
            if (state.basicTDHCalculation) {
                state.basicTDHCalculation = {
                    ...state.basicTDHCalculation,
                    newTDH: action?.payload,
                }
            }
        },
        setIPRChartData: (state, action) => {
            state.iprCurve = action?.payload;
        },
        setDepletionChartData: (state, action) => {
            state.depletionCurve = action?.payload;
        },
        setShowIPRPlotInMainReport: (state, action) => {
            state.showIPRPlotInMainReport = action?.payload;
        },
        setShowDepletionPlotInMainReport: (state, action) => {
            state.showDepletionPlotInMainReport = action?.payload;
        },
        setBasicSummary: (state, action) => {
            state.basicSummary = action?.payload;
        },
        setbasicSummaryScenarios: (state, action) => {
            state.basicSummaryScenarios = action?.payload;
        },
        setFormattedBasicSummaryScenarios: (state, action) => {
            const getVal = (item: any, keys: string[]) => {
                if (!item) return "";
                for (const k of keys) {
                    const v = item[k];
                    if (v != null && v !== "NaN" && !Number.isNaN(Number(v))) return Number(v).toFixed(2);
                }
                return "";
            };
            state.formattedBasicSummaryScenarios = action?.payload?.length > 0 ? [
                {
                    label: `${LABELS?.matchProductionRate} (${LABELS?.blpd})`,
                    ...Object.fromEntries(
                        action?.payload?.map((item: any, index: any) => [`value${index + 1}`, getVal(item, ["matchProductionRateFromGraph", "matchProdRate"])])
                    ),
                },
                {
                    label: `${LABELS?.matchPowerFluidPressure} (${LABELS?.psia})`,
                    ...Object.fromEntries(
                        action?.payload?.map((item: any, index: any) => [`value${index + 1}`, getVal(item, ["matchPowerFluidRate", "matchPwrFluidPress"])])
                    ),
                },
                {
                    label: `${LABELS?.matchPowerFluidRate} (${LABELS?.blpd})`,
                    ...Object.fromEntries(
                        action?.payload?.map((item: any, index: any) => [`value${index + 1}`, getVal(item, ["matchQn", "matchPwrFluidRate"])])
                    ),
                },
                {
                    label: `${LABELS?.matchPumpIntakePressure} (${LABELS?.psia})`,
                    ...Object.fromEntries(
                        action?.payload?.map((item: any, index: any) => [`value${index + 1}`, getVal(item, ["matchIntakeFromGraph", "matchPumpIntakePres"])])
                    ),
                },
                {
                    label: `${LABELS?.pumpDischargePressure} (${LABELS?.psia})`,
                    ...Object.fromEntries(
                        action?.payload?.map((item: any, index: any) => [`value${index + 1}`, getVal(item, ["discharge", "pumpDischargePrs"])])
                    ),
                },
                {
                    label: `${LABELS?.matchPowerFluidPressureAtPump} (${LABELS?.psia})`,
                    ...Object.fromEntries(
                        action?.payload?.map((item: any, index: any) => [`value${index + 1}`, getVal(item, ["matchPn", "matchPwrFldPrsAtPmp"])])
                    ),
                },
            ] : [];
        },
        setCavitationWarning: (state, action) => {
            state.cavitationWarning = action?.payload;
        },
    },
});
export const {
    resetBasicCalculations,
    setLoading,
    setError,
    setBasicWellInflowCalculation,
    setBasicWellInflowCalculation2,
    setBasicTDHCalculation,
    setBasicTDHCalculationOutput,
    setBasicWellIPRDepletionPressure,
    setBasicWellIPRDepletionPercentage,
    setBasicWellIPRFutureProdRate,
    setBasicWellIPRFutureBHP,
    setBasicCalculationComplete,
    setBasicWellInflowData,
    setBasicIPRMethodologySelection,
    setBasicTDHDesiredPIP,
    setBasicNewTDH,
    setIPRChartData,
    setDepletionChartData,
    setShowIPRPlotInMainReport,
    setShowDepletionPlotInMainReport,
    setBasicSummary,
    setbasicSummaryScenarios,
    setFormattedBasicSummaryScenarios,
    setCavitationWarning,
} = BasicCalculationSlice.actions;
export default BasicCalculationSlice.reducer;
